import React from "react";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";

import Dot from "../Board/Dot";
import LineChart from "../../UserTimeline/TimelineItem/LineChart";
import CustomStackedAreaChart from "../CustomStackedAreaChart";
import { groupBy, roundToTwoDecimalPlaces } from "../../tools";
import ChooseLabels from "../ChooseLabels";
import { getLabelId } from "../moodboardModel";

/**
 * Returns the integrated data to plot the lineChart (go from many datapoints to fewer datapoints)
 * @param {Object[]} lineChart - The unintegrated lineChart data
 * @param {Object[]} quantitativeLabelsChecked - The selected quantiative labels
 * @param {string} quantitativeLabelsChecked[].id - The id of the selected label
 * @param {string} quantitativeLabelsChecked[].title - The title of the selected label
 * @param {Object[]} qualitativeLabelsChecked - The selected qualitative labels
 * @param {string} qualitativeLabelsChecked[].id - The id of the qualitative label
 * @param {string} qualitativeLabelsChecked[].title - The title of the qualitative label
 * @param {string} smoother - Day or week integration
 * @returns {Object[]} Integrated lineChart data
 */
const getIntegration = ({
  lineChart,
  initData,
  quantitativeLabelsChecked,
  qualitativeLabelsChecked,
  smoother,
}) => {
  const t = initData.map((beep) => Math.round(beep.time / 1000)); // convert time to seconds
  const relD = lineChart.flatMap((item) =>
    quantitativeLabelsChecked.map(({ id }) => item[id + "#relative"] ?? NaN)
  );
  const absD = lineChart.flatMap((item) =>
    quantitativeLabelsChecked.map(({ id }) =>
      item[id + "#type"] === "count" ? 1 : item[id + "#absolute"] ?? NaN
    )
  );
  const c = quantitativeLabelsChecked.map(({ type }) =>
    type === "count" ? 1 : 0
  ); // selected
  const m = (function () {
    if (smoother === "day") return 1;
    if (smoother === "week") return 2;
    return -1;
  })();
  const relBins = window.getBins(t, relD, c, m);
  const absBins = window.getBins(t, absD, c, m);

  const nBins = relBins.NBins;
  const nCols = relBins.NCols;
  /*   console.log({
    function: "window.getBins(t,d,c,m)",
    input: { t, d: relD, c, m },
    output: relBins,
  }); */
  let newData = [];
  for (let i = 0; i < nBins; i++) {
    const time = relBins.middleBinReturn[i] * 1000; // new time point in ms
    const mergedBeeps = relBins.idxMap
      .reduce((a, idxElement, idxIndex) => {
        if (idxElement === i) a.push(idxIndex);
        return a;
      }, []) // all indexes of merged beeps
      .flatMap((index) => initData[index].table); // get the qualitative beeps
    const grouped = groupBy(mergedBeeps, (answer) => answer.id);

    let quanti = {}; // new (relative) quantitative value
    for (let j = 0; j < nCols; j++) {
      const id = quantitativeLabelsChecked[j].id;
      const title = quantitativeLabelsChecked[j].label;
      const relValue = roundToTwoDecimalPlaces(
        relBins.binReturn[i + j * nBins]
      );
      const type = grouped.get(id)?.[0].type; // get the type from the first merged beep

      let absValue = roundToTwoDecimalPlaces(absBins.binReturn[i + j * nBins]);
      if (type === "time") {
        function convertDecimalToHoursMinutes(decimalHours) {
          const hours = Math.floor(decimalHours);
          const minutes = Math.round((decimalHours - hours) * 60);

          return `${hours}:${minutes}`;
        }
        absValue = convertDecimalToHoursMinutes((relValue / 100) * 24);
      }

      if (!isNaN(relValue)) {
        Object.assign(quanti, {
          [id + "#id"]: id,
          [id + "#title"]: title,
          [id + "#relative"]: relValue,
          [id + "#absolute"]: absValue,
          [id + "#type"]: type,
        });
      }
    }

    let quali = {};
    // group the labels
    for (let j = 0; j < qualitativeLabelsChecked.length; j++) {
      const id = qualitativeLabelsChecked[j].id;
      const title = qualitativeLabelsChecked[j].label;
      const answers = grouped.get(id);
      if (answers?.length > 0) {
        const type = answers[0].type; // get the type of the qualitative answer
        //console.log(answers);
        const scores = answers.flatMap(({ scoreArr }) => scoreArr); // get all the relative scores
        //console.log(scores);
        const occurrences = scores.reduce(function (acc, curr) {
          return acc[curr] ? ++acc[curr] : (acc[curr] = 1), acc;
        }, {}); // count the occurence of a score
        const relValue = Object.keys(occurrences)
          .map((key) =>
            occurrences[key] === 1 ? key : `${occurrences[key]}x ${key}`
          )
          .join(", "); // display the occurences of the answers
        Object.assign(quali, {
          [id + "#id"]: id,
          [id + "#title"]: title,
          [id + "#relative"]: relValue,
          [id + "#type"]: type,
        });
      }
    }
    newData.push({ ...quanti, ...quali, time });
  }
  // visualize button clicks in day and week mode
  const buttonLabels = quantitativeLabelsChecked.filter(
    ({ type }) => type === "count"
  );
  let buttonMax = {}; // keys are buttonIds and values are button counts
  // search the max for each button
  buttonLabels.forEach((buttonLabel) => {
    let max = 0;
    newData.forEach((item) => {
      const absoluteCount = item[buttonLabel.id + "#absolute"];
      if (absoluteCount > max) max = absoluteCount;
    });
    buttonMax[buttonLabel.id] = max;
  });
  // fill in relative button value
  newData.forEach((item) => {
    Object.keys(buttonMax).forEach((buttonId) => {
      item[buttonId + "#relative"] =
        (item[buttonId + "#absolute"] / buttonMax[buttonId]) * 100;
    });
  });
  return newData;
};

/**
 * Returns the full stacked lineChart
 * @param {Object[]} data - The (un)integrated lineChart data
 * @param {Object[]} quantitativeLabelsChecked - The selected quantiative labels
 * @param {string} quantitativeLabelsChecked[].id - The id of the selected label
 * @param {string} quantitativeLabelsChecked[].title - The title of the selected label
 * @returns {Object[]} Full stacked lineChart
 */
const getStacked = ({ data, quantitativeLabelsChecked }) => {
  // get all the absolute values of the quantiative answers
  const absD = data.map((item) =>
    quantitativeLabelsChecked.map(({ id }) =>
      item[id + "#type"] === "time"
        ? item[id + "#relative"] ?? NaN
        : item[id + "#absolute"] ?? NaN
    )
  );
  // calculate the sum of all items on a particular timepoint
  const totalD = absD.map((item) =>
    item.length > 0 ? item.reduce((prev, current) => prev + current) : []
  );
  //console.log(totalD);
  // add new id#stacked key
  const newData = data.map((item, index) => {
    let obj = { ...item };
    quantitativeLabelsChecked.forEach(({ id }) => {
      const val =
        obj[id + "#type"] === "time"
          ? obj[id + "#relative"] ?? NaN
          : obj[id + "#absolute"] ?? NaN;
      const average = (val / totalD[index]) * 100;
      if (!isNaN(average)) {
        obj[id + "#stacked"] = roundToTwoDecimalPlaces(average);
      }
    });
    return obj;
  });
  return newData;
};

function TimelineBoard(props) {
  const {
    lineChart,
    lineChartWithMissings,
    table,
    data: initData,
    quantitativeLabelsChecked: quanti,
    qualitativeLabelsChecked: quali,
    selectedLabels,
    handleSelectLabels,
    variant,
    labelOpacity,
    handleMouseEnter,
    handleMouseLeave,
    time,
    fullStacked,
    smoother,
    initialMoodboard,
    connectLine,
  } = props;
  const buttonLabels = quali.filter(({ type }) => type === "count");
  const quantiChecked = quanti.filter(({ id }) => selectedLabels.includes(id)); // don't include buttons
  const quantitativeLabelsChecked =
    smoother === "time" ? quantiChecked : [...buttonLabels, ...quantiChecked]; // only include buttons if view is day or week
  const qualiChecked = quali.filter(({ id }) => selectedLabels.includes(id));
  const qualitativeLabelsChecked =
    smoother === "time"
      ? qualiChecked
      : qualiChecked.filter(({ type }) => type !== "count"); // only include qualitative count labels if view is day or week

  // Returns the data to plot the lineChart
  const getData = () => {
    let data = [];
    if (smoother === "time") {
      // show all beeps (no integration)
      data = connectLine ? lineChart : lineChartWithMissings;
    } else {
      // integrate over days or weeks
      data = getIntegration({
        lineChart: lineChart,
        initData,
        quantitativeLabelsChecked,
        qualitativeLabelsChecked,
        smoother,
      });
    }
    // display area chart
    if (fullStacked) {
      data = getStacked({
        data,
        quantitativeLabelsChecked,
      });
    }
    return data;
  };

  // Returns LineChart or CustomStackedAreaChart component
  function Chart() {
    const data = getData();
    if (fullStacked)
      return (
        <CustomStackedAreaChart
          data={data}
          quantitativeLabelsChecked={quantitativeLabelsChecked}
          qualitativeLabelsChecked={qualitativeLabelsChecked}
          height={200}
        />
      );

    return (
      <LineChart
        data={data}
        time={time}
        quantitativeLabelsChecked={quantitativeLabelsChecked}
        qualitativeLabelsChecked={qualitativeLabelsChecked}
        opacity={labelOpacity}
        height={200}
        connectLine={connectLine}
      />
    );
  }

  function Labels() {
    return (
      <div style={{ display: "flex", alignItems: "center" }}>
        <div style={{ height: 20, overflow: "hidden" }}>
          <Grid container>
            {quantitativeLabelsChecked.map((label) => (
              <Grid
                item
                key={label.id}
                onMouseEnter={() => handleMouseEnter(label.id)}
                onMouseLeave={handleMouseLeave}
              >
                <div
                  style={{
                    display: "flex",
                    marginRight: 12,
                    textAlign: "left",
                  }}
                >
                  <div>
                    <Dot label={label.id} />
                  </div>
                  <Tooltip title={label.question}>
                    <Typography noWrap variant="body2">
                      {label.label}
                    </Typography>
                  </Tooltip>
                </div>
              </Grid>
            ))}
            {qualitativeLabelsChecked.map((label) => (
              <Grid item key={label.id}>
                <Tooltip title={label.question}>
                  <Typography
                    key={label.id}
                    variant="body2"
                    style={{ marginRight: 12 }}
                    noWrap
                  >
                    {label.label}
                  </Typography>
                </Tooltip>
              </Grid>
            ))}
          </Grid>
        </div>
        <div style={{ minWidth: "fit-content" }}>
          <ChooseLabels
            variant={variant}
            initTable={table}
            selectedLabels={selectedLabels}
            handleSelectLabels={handleSelectLabels}
            initialSelectedLabels={initialMoodboard.labelProps.map((prop) =>
              getLabelId(prop)
            )}
          />
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <Chart />
      <Labels />
    </React.Fragment>
  );
}

export default TimelineBoard;
